import React, { memo, useCallback, useMemo, useState, useRef } from 'react';
import { Button, Card } from 'components/ui';
import Panel1Img from './1.png';
import Panel2Img from './2.png';
import Panel3Img from './3.png';
import Panel4Img from './4.png';
import bg from './bg.jpg';
import { useUserContext } from '@app/providers';
import { ModalTenderCreate } from '@app/modals';
import { TenderCategory } from '@app/models';
import { useNotification } from '@app/providers';

function WelcomeBanner() {
  const { user, showSignInModal, company } = useUserContext();
  const [tenderCategory, setTenderCategory] = useState<TenderCategory>(
    TenderCategory.CONTRACTOR
  );
  const [modalTenderCreateVisible, setModalTenderCreateVisible] =
    useState<boolean>(false);
  const { showNotification } = useNotification();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleTenderCreateSuccess = useCallback(() => {
    showNotification({ message: 'Заявка создалась', variant: 'success' });
  }, [showNotification]);

  const panels = useMemo(
    () => [
      {
        title: 'СМР',
        text: 'Поиск подряда для Вас и Вашей компании',
        src: Panel1Img,
        tenderCategory: TenderCategory.CONTRACTOR,
      },
      {
        title: 'Материалы',
        text: 'Выберите подходящий материал',
        src: Panel2Img,
        tenderCategory: TenderCategory.SUPPLIER,
      },
      {
        title: 'Спецтехника',
        text: 'Выберите подходящую спецтехнику',
        src: Panel3Img,
        tenderCategory: TenderCategory.VEHICLE,
      },
      {
        title: 'Специалисты',
        text: 'Выберите подходящих специалистов',
        src: Panel4Img,
        tenderCategory: TenderCategory.WORKER,
      },
    ],
    []
  );

  const onClickPanel = useCallback(
    (category: TenderCategory) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!user.authenticated) {
        showSignInModal();
        return;
      }
      setTenderCategory(category);
      setModalTenderCreateVisible(true);
    },
    [showSignInModal, user]
  );

  const closeModalTenderCreate = useCallback(() => {
    setModalTenderCreateVisible(false);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (scrollContainerRef.current) {
      scrollContainerRef.current.dataset.startX = touch.clientX.toString();
      scrollContainerRef.current.dataset.startScrollLeft =
        scrollContainerRef.current.scrollLeft.toString();
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!scrollContainerRef.current?.dataset.startX) return;

    const touch = e.touches[0];
    const startXStr = scrollContainerRef.current.dataset.startX;
    const startScrollLeftStr =
      scrollContainerRef.current.dataset.startScrollLeft;

    if (!startXStr || !startScrollLeftStr) return;

    const startX = parseInt(startXStr);
    const startScrollLeft = parseInt(startScrollLeftStr);
    const diff = startX - touch.clientX;

    scrollContainerRef.current.scrollLeft = startScrollLeft + diff;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (scrollContainerRef.current) {
      delete scrollContainerRef.current.dataset.startX;
      delete scrollContainerRef.current.dataset.startScrollLeft;
    }
  }, []);

  return (
    <>
      <div
        className="relative w-full mt-[-16px] mb-8 overflow-hidden"
        style={{ backgroundColor: '#2563eb' }}
      >
        <div
          className="absolute inset-0 z-0 bg-cover bg-no-repeat bg-center"
          style={{
            backgroundImage: `url(${bg})`,
            backgroundPosition: '0 72%',
          }}
        />
        <div className="absolute inset-0 z-0 bg-black/50" />
        <div className="relative z-0 max-w-7xl mx-auto w-full px-2 sm:px-4 py-10 md:py-12">
          <p className="font-medium text-lg xs:text-xl md:text-3xl leading-7 md:leading-9 text-center text-white mb-2 md:mb-3 tracking-tight">
            Сократи время на поиск субподрядной организации, строительной
            бригады или специалиста
          </p>
          <p className="font-normal text-xs xs:text-sm md:text-lg leading-5 md:leading-6 text-center text-white mb-6 md:mb-16 tracking-wide hidden sm:block">
            Детальные предложения от подрядчиков, размещение заказов, выбор
            подходящих исполнителей
          </p>

          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-y-3 gap-x-4">
            {panels.map((panel, panelIndex) => (
              <Card
                key={panelIndex}
                className="flex items-center p-2 xs:p-3 rounded-xl bg-white h-full min-h-[100px] w-full max-w-full"
              >
                <div className="flex-shrink-0">
                  <img
                    src={panel.src}
                    alt=""
                    className="w-[56px] xs:w-[72px] md:w-[100px] max-w-full"
                  />
                </div>
                <div className="flex flex-col flex-grow ml-2 xs:ml-3 h-full">
                  <p className="font-medium text-xs xs:text-sm md:text-base text-[#111a27] leading-5 m-0">
                    {panel.title}
                  </p>
                  <p className="font-normal text-[10px] xs:text-xs md:text-sm text-[#111a27] leading-5 mt-1 mb-2 md:mb-3">
                    {panel.text}
                  </p>
                  <div className="rounded-lg mt-auto">
                    <Button asChild>
                      <button
                        className="w-full font-bold text-base py-2 rounded-lg transition-colors"
                        onClick={onClickPanel(panel.tenderCategory)}
                      >
                        Подать заявку
                      </button>
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
      <ModalTenderCreate
        companyId={!!company ? company.id : null}
        visible={modalTenderCreateVisible}
        closeModal={closeModalTenderCreate}
        tenderCategory={tenderCategory}
        onSuccess={handleTenderCreateSuccess}
      />
    </>
  );
}

export default memo(WelcomeBanner);
